import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Typography } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';

import { groupParentSupervisionActions } from '../../../../store';
import { FBoxContainer, FCheckbox, FHTextarea, FHWarning } from '../../../base';
import {
  FORTA_GRAY4,
  FORTA_BLUE,
  FORTA_ERROR,
  FORTA_WHITE,
  FORTA_BLACK,
  FORTA_YELLOW_2,
  FORTA_YELLOW,
  FORTA_BROWN,
  FORTA_SUCCESS,
  FORTA_SUCCESS_2,
} from '../../../../utils/colors';
import { GROUP_SN_FIELD_MIN_LENGTH } from '../constants';
import { formatSecondsToHHMM3 } from '../../../../helpers';
import { checkAllTopicMapError } from './utils';

import type { IFamilyAttendance, ITrainingTopic } from '../../../../utils/types';
import type { RootState } from '../../../../store';
import type { ChangeEvent } from 'react';

interface FamilyResponseItemProps {
  attendance: IFamilyAttendance;
  isEditable: boolean;
  validate: boolean;
  sessionTopics: ITrainingTopic[];
  disabled: boolean;
  groupSNId?: number;
  isSessionFullyAttended: boolean;
  familyAttendanceDuration: number;
  sessionDuration: number;
  onUpdate: (updatedAttendace: IFamilyAttendance) => void;
}

interface FamiliesResponseProps {
  isEditable: boolean;
  validate: boolean;
}

function FamilyResponseItem({
  attendance,
  isEditable,
  disabled,
  validate,
  sessionTopics,
  groupSNId,
  familyAttendanceDuration,
  sessionDuration,
  isSessionFullyAttended,
  onUpdate,
}: FamilyResponseItemProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const [familyResponse, setFamilyResponse] = useState<string>('');
  const [touched, setTouched] = useState<boolean>(false);

  const title = `${attendance.client?.first_name} ${attendance.client?.last_name}`;
  const familyTopics = attendance.topics;
  const response = attendance.response;

  let topicWarning = '';
  if (isSessionFullyAttended && sessionTopics?.length !== familyTopics?.length) {
    topicWarning =
      'Family attended the full session. Please review and select all topics attended by the family before submitting.';
  }
  if (!isSessionFullyAttended && sessionTopics?.length === familyTopics?.length) {
    topicWarning =
      'Family attended the session partially. Please select only the topics they participated in before submitting.';
  }

  const topicSummary = useMemo(() => {
    if (!familyTopics?.length) return '';
    let summary = isSessionFullyAttended
      ? `The parent/caregiver of ${title} attended the full session. During their participation, the following topics were addressed: `
      : `The parent/caregiver of ${title} a portion of the session. During their participation, the following topics were addressed: `;
    summary +=
      familyTopics.length > 1
        ? familyTopics.reduce(
            (str, item, index) =>
              str +
              item.label +
              (index === familyTopics.length - 2
                ? ' and '
                : index === familyTopics.length - 1
                  ? ''
                  : ', '),
            '',
          )
        : familyTopics[0].label;
    return `${summary}.`;
  }, [familyTopics.length]);

  const noTopicError = familyTopics.length === 0;
  const allTopicError = checkAllTopicMapError(attendance, sessionTopics, sessionDuration);
  useEffect(() => {
    setFamilyResponse(response);
  }, [response]);

  const showError = !familyResponse || familyResponse?.trim()?.length < GROUP_SN_FIELD_MIN_LENGTH;

  const handleAddFamilyTopic = (topicId: number) => {
    const data = { training_topic_id: topicId };
    dispatch(
      groupParentSupervisionActions.createTopicFamilyMapping({
        getAccessTokenSilently,
        attendanceId: attendance.id,
        groupSNId: groupSNId,
        data,
      }),
    );
  };

  const handleRemoveFamilyTopic = (topicId: number) => {
    dispatch(
      groupParentSupervisionActions.deleteTopicFamilyMapping({
        getAccessTokenSilently,
        attendanceId: attendance.id,
        groupSNId: groupSNId,
        topicId,
      }),
    );
  };

  return (
    <FBoxContainer sx={{ mt: 3, backgroundColor: FORTA_WHITE }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography
          fontWeight={700}
          color={FORTA_BLUE.primary}
          mb={3}
          fontSize={21}
          textAlign="left"
        >
          {title}
        </Typography>
        <Typography
          sx={{
            backgroundColor: isSessionFullyAttended ? FORTA_SUCCESS_2 : FORTA_YELLOW_2,
            border: `2px solid ${isSessionFullyAttended ? FORTA_SUCCESS : FORTA_YELLOW}`,
            height: '30px',
            fontWeight: 700,
            color: isSessionFullyAttended ? FORTA_BLUE.primary : FORTA_BROWN,
            px: 1,
            py: 0.3,
            lineHeight: '21px',
            borderRadius: '10px',
          }}
        >
          {isSessionFullyAttended
            ? `Full Attendance`
            : `Partial Attendance (${formatSecondsToHHMM3(
                familyAttendanceDuration,
              ).trim()} out of ${formatSecondsToHHMM3(sessionDuration).trim()})`}
        </Typography>
      </Box>

      {sessionTopics.length > 0 && (
        <>
          <Typography textAlign="left" mb={0.5} fontWeight={700}>
            Choose topics attended by the parents.
          </Typography>
          {topicWarning && isEditable && (
            <FHWarning
              sxText={{ ml: 1, fontSize: 16, color: FORTA_BLUE.primary, fontWeight: 700 }}
              sxContainer={{ ml: -0.5, backgroundColor: 'transparent', p: 0 }}
              content={topicWarning}
            />
          )}
          {noTopicError && (
            <Typography color={FORTA_ERROR} sx={{ mt: 2 }} textAlign="left">
              Please select at least one topic
            </Typography>
          )}
          {allTopicError && (
            <Typography color={FORTA_ERROR} sx={{ mt: 2 }} textAlign="left">
              Please review the topics selected. Family attendance time is not sufficient to cover
              all topics.
            </Typography>
          )}
          <Grid
            container
            sx={{
              py: 2,
              pb: 0,
              mb: 2,
              borderRadius: '8px',
            }}
          >
            {sessionTopics.map((topic) => {
              const familyTopic = familyTopics.find((t) => t.training_topic_id === topic.id);
              return (
                <Grid item lg={4} md={6} sm={12} key={topic.id}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <FCheckbox
                      label={topic.label}
                      checked={Boolean(familyTopic)}
                      disabled={disabled || !isEditable}
                      coloredBackground
                      onChange={(e) => {
                        if (e.target.checked) {
                          handleAddFamilyTopic(topic.id);
                        } else {
                          if (familyTopic) {
                            handleRemoveFamilyTopic(familyTopic.id);
                          }
                        }
                      }}
                      iconStyles={{
                        border:
                          noTopicError || allTopicError
                            ? `1px solid ${FORTA_ERROR}`
                            : `1px solid ${FORTA_BLUE.light1}`,
                      }}
                      sx={{
                        mb: 2,
                        fontSize: '16px !important',
                        width: 'fit-content',
                        color: noTopicError || allTopicError ? FORTA_ERROR : FORTA_BLACK,
                      }}
                    />
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}
      <Typography textAlign="left" mb={0.5} fontWeight={700}>
        Family response to training
      </Typography>
      {isEditable && (
        <>
          {!noTopicError && (
            <Box
              sx={{
                backgroundColor: FORTA_BLUE.light3,
                px: 2,
                pt: 2,
                pb: 1,
                borderRadius: '8px 8px 0 0',
                border: `1px solid ${FORTA_BLUE.light1}`,
                borderBottom: 'none',
                cursor: 'not-allowed',
              }}
            >
              <Typography textAlign="left" color={FORTA_GRAY4} mb={0.5}>
                {topicSummary}
              </Typography>
            </Box>
          )}
          <FHTextarea
            data-testid={`familyResponseTextBox_${title.split(' ').join('_')}`}
            disabled={disabled}
            sx={{
              boxShadow: 'none',
              pt: 0,
              borderRadius: topicSummary ? '0 0 8px 8px' : undefined,
              borderTop: topicSummary ? `1px dashed ${FORTA_BLUE.light1}` : undefined,
            }}
            minRows={4}
            maxRows={20}
            coloredBackground
            placeholder="Describe the family response to training"
            expandable={!disabled}
            value={familyResponse}
            error={(touched || validate) && showError}
            onEdit={(e: ChangeEvent<HTMLTextAreaElement>) => {
              setFamilyResponse(e.target.value);
              if (!touched) {
                setTouched(true);
              }
            }}
            onBlur={() => {
              if (
                response === familyResponse ||
                (familyResponse?.length && familyResponse?.length < GROUP_SN_FIELD_MIN_LENGTH)
              ) {
                return;
              }
              onUpdate({
                ...attendance,
                response: familyResponse,
              });
            }}
            onCloseExpandDialog={() => {
              if (
                response === familyResponse ||
                (familyResponse?.length && familyResponse?.length < GROUP_SN_FIELD_MIN_LENGTH)
              ) {
                return;
              }
              onUpdate({
                ...attendance,
                response: familyResponse,
              });
            }}
            helperText={
              <Typography
                color={showError ? FORTA_ERROR : FORTA_BLUE.primary}
                fontSize={14}
                mt={0.5}
              >
                {`Minimum ${GROUP_SN_FIELD_MIN_LENGTH} characters`}
              </Typography>
            }
          />
        </>
      )}
      {!isEditable && (
        <Box>
          <Typography color={FORTA_GRAY4} textAlign="left" mt={0.5}>
            {topicSummary}
          </Typography>
          <Typography color={FORTA_GRAY4} textAlign="left">
            {response}
          </Typography>
        </Box>
      )}
    </FBoxContainer>
  );
}

function FamiliesResponse({ isEditable, validate }: FamiliesResponseProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { current: groupParentSN, loading: groupSNLoading } = useSelector(
    (state: RootState) => state.groupParentSupervision,
  );

  const [familyAttendance, setFamilyAttendanceItem] = useState<IFamilyAttendance[]>([]);

  useEffect(() => {
    if (!groupParentSN) {
      setFamilyAttendanceItem([]);
      return;
    }
    setFamilyAttendanceItem(groupParentSN.family_attendance ?? []);
  }, [groupParentSN]);

  const handleResponseUpdate = (updatedAttendance: IFamilyAttendance) => {
    if (!groupParentSN) return;
    const data = {
      response: updatedAttendance.response ? updatedAttendance.response : null,
      date_start: updatedAttendance.date_start,
      date_end: updatedAttendance.date_end,
    };
    dispatch(
      groupParentSupervisionActions.updateFamilyAttendance({
        getAccessTokenSilently,
        groupSNId: groupParentSN.id,
        attendanceId: updatedAttendance.id,
        data,
      }),
    );
  };

  return familyAttendance.length > 0 ? (
    <FBoxContainer sx={{ flexGrow: 0, mt: 3, boxShadow: 'none' }}>
      <>
        <Typography fontSize={21} color={FORTA_BLUE.primary} textAlign={'left'}>
          Family response to training
        </Typography>
        <Box mt={1}>
          {familyAttendance.map((item) => (
            <FamilyResponseItem
              validate={validate}
              disabled={groupSNLoading}
              key={item.id}
              attendance={item}
              isSessionFullyAttended={groupParentSN?.duration === item.duration}
              groupSNId={groupParentSN?.id}
              familyAttendanceDuration={item.duration}
              sessionDuration={groupParentSN?.duration ?? 0}
              sessionTopics={groupParentSN?.training_topics ?? []}
              isEditable={isEditable}
              onUpdate={(updatedAttendace) => handleResponseUpdate(updatedAttendace)}
            />
          ))}
        </Box>
      </>
    </FBoxContainer>
  ) : null;
}

export default FamiliesResponse;
